import { useState, type FormEvent } from "react";
import { Box, Button, Paper, Typography, Stack, Alert, CircularProgress, TextField } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { resetPassword } from "@/api/auth";
import { setLocalAuthSession } from "@/localAuth";
import { getErrorMessage } from "@/utils/getErrorMessage";
import athrivIcon from "@/assets/athriv-icon.png";

/** Reached via the link emailed by ForgotPasswordPage's request - not part of
 * the authenticated router, since whoever lands here has no session yet. See
 * App.tsx for the routing. */
export default function ResetPasswordPage() {
  const params = new URLSearchParams(window.location.search);
  const email = params.get("email") ?? "";
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => resetPassword({ email, token, password }),
    onSuccess: (data) => {
      setLocalAuthSession(data);
      window.location.href = "/";
    },
    onError: (err: unknown) => setError(getErrorMessage(err))
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    mutation.mutate();
  }

  if (!email || !token) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Alert severity="error">This reset link is missing required information.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", px: 2 }}>
      <Paper elevation={0} sx={{ p: 5, width: 380, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
        <Stack spacing={3} alignItems="center" textAlign="center">
          <Box component="img" src={athrivIcon} alt="Athriv Technologies" sx={{ width: 48, height: 48, objectFit: "contain" }} />
          <Box>
            <Typography variant="h5" sx={{ fontFamily: '"Manrope", sans-serif' }}>Set a new password</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Choose a new password for {email}.
            </Typography>
          </Box>
          <Stack component="form" spacing={2} sx={{ width: "100%" }} onSubmit={handleSubmit}>
            <TextField
              label="New password" type="password" size="small" fullWidth required
              value={password} onChange={(e) => setPassword(e.target.value)}
              helperText="At least 8 characters."
            />
            <TextField
              label="Confirm password" type="password" size="small" fullWidth required
              value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <Button fullWidth variant="contained" type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? <CircularProgress size={18} color="inherit" /> : "Reset Password & Log In"}
            </Button>
          </Stack>
          {error && <Alert severity="error" sx={{ width: "100%", textAlign: "left" }}>{error}</Alert>}
        </Stack>
      </Paper>
    </Box>
  );
}
